import { ReactNode } from "react";
import classNames from "classnames";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  AlignCenterIcon,
  AlignJustifyIcon,
  AlignLeftIcon,
  AlignRightIcon,
  AttributeIcon,
  BulletListIcon,
  CodeIcon,
  HighlightIcon,
  IndentIcon,
  LinkIcon,
  NumberedListIcon,
  OutdentIcon,
  RedoIcon,
  StrikethroughIcon,
  TextIcon,
  UndoIcon,
  UnlinkIcon,
} from "../../icons";
import { FontFamilies, FontSizes } from "../../config/formEnums";
import { Align, BlockOptions, BlockState, DefaultBlockState, ListTag } from "./richTextBlocks";
import { RichTextColourPicker } from "./RichTextColourPicker";

export interface ToolbarState extends BlockState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  size: string;
  family: string;
  colour: string;
  background: string;
}

export const DefaultToolbarState: ToolbarState = {
  ...DefaultBlockState,
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  size: "",
  family: "",
  colour: "",
  background: "",
};

export type ToggleKey = "bold" | "italic" | "underline" | "strike";

export interface ToolbarActions {
  undo: () => void;
  redo: () => void;
  toggleHtml: () => void;
  block: (tag: string) => void;
  indent: () => void;
  outdent: () => void;
  align: (align: Align) => void;
  size: (size: string) => void;
  family: (family: string) => void;
  toggle: (key: ToggleKey) => void;
  colour: (colour: string | null) => void;
  background: (colour: string | null) => void;
  list: (tag: ListTag) => void;
  link: () => void;
  unlink: () => void;
  attribute: () => void;
}

interface RichTextToolbarProps {
  label: string;
  htmlMode: boolean;
  state: ToolbarState;
  canUndo: boolean;
  canRedo: boolean;
  hasAttributes: boolean;
  actions: ToolbarActions;
}

// As the canvas's screen size bar: both carry a border, the quiet one in the bar's own white.
const ButtonClasses =
  "btn btn-sm shadow-none d-flex align-items-center justify-content-center gap-2 rounded-1 p-2 lh-1";
const BarButton = classNames(ButtonClasses, "border border-white bg-transparent text-body-secondary hover-bg-light");
const BarButtonCurrent = classNames(ButtonClasses, "border bg-light text-dark");

const Toggles: { key: ToggleKey; label: string; letter: string; className?: string }[] = [
  { key: "bold", label: "Bold", letter: "B", className: "fw-bold" },
  { key: "italic", label: "Italic", letter: "I", className: "fst-italic" },
  { key: "underline", label: "Underline", letter: "U", className: "text-decoration-underline" },
];

const Aligns: { align: Align; label: string; icon: IconDefinition }[] = [
  { align: "left", label: "Align left", icon: AlignLeftIcon },
  { align: "center", label: "Align centre", icon: AlignCenterIcon },
  { align: "right", label: "Align right", icon: AlignRightIcon },
  { align: "justify", label: "Justify", icon: AlignJustifyIcon },
];

const familyName = (family: string) => family.split(",")[0].replace(/["']/g, "").trim();

const SelectClass = "form-select w-auto";

// Choosing from a list takes the focus, so the buttons beside it keep it in the text.
const keepFocus = { onMouseDown: (event: { preventDefault: () => void }) => event.preventDefault() };

function Row({ children, divided = true }: { children: ReactNode; divided?: boolean }) {
  return (
    <div className={classNames("d-flex flex-wrap align-items-center gap-1 w-100", { "border-top pt-1": divided })}>
      {children}
    </div>
  );
}

/** The rich text editor's buttons, a row to each kind as in the old editor. What is pressed comes from the text. */
export function RichTextToolbar({ label, htmlMode, state, canUndo, canRedo, hasAttributes, actions }: RichTextToolbarProps) {
  const button = (name: string, icon: IconDefinition, onClick: () => void, options: { pressed?: boolean; disabled?: boolean } = {}) => (
    <button
      key={name}
      type="button"
      className={options.pressed ? BarButtonCurrent : BarButton}
      aria-label={name}
      aria-pressed={options.pressed}
      title={name}
      disabled={options.disabled}
      onClick={onClick}
      {...keepFocus}
    >
      <FontAwesomeIcon icon={icon} className="fa-lg" />
    </button>
  );

  return (
    <div
      className="d-flex flex-column gap-1 bg-white border-bottom rounded-top-2 p-1 w-100"
      role="toolbar"
      aria-label={`${label} formatting`}
    >
      <Row divided={false}>
        {!htmlMode && (
          <>
            {button("Undo", UndoIcon, actions.undo, { disabled: !canUndo })}
            {button("Redo", RedoIcon, actions.redo, { disabled: !canRedo })}
          </>
        )}
        <button
          type="button"
          className={htmlMode ? BarButtonCurrent : BarButton}
          aria-label="HTML"
          aria-pressed={htmlMode}
          title="Edit the HTML"
          onClick={actions.toggleHtml}
          {...keepFocus}
        >
          <FontAwesomeIcon icon={CodeIcon} className="fa-lg" />
        </button>
        {!htmlMode && (
          <select
            className={SelectClass}
            aria-label="Text style"
            title="Text style"
            value={state.block}
            onChange={(event) => actions.block(event.target.value)}
          >
            {BlockOptions.map(({ Key, Value }) => (
              <option key={Key} value={Key}>
                {Value}
              </option>
            ))}
          </select>
        )}
      </Row>
      {!htmlMode && (
        <>
          <Row>
            {button("Decrease indent", OutdentIcon, actions.outdent)}
            {button("Increase indent", IndentIcon, actions.indent)}
            {Aligns.map(({ align, label: name, icon }) => button(name, icon, () => actions.align(align), { pressed: state.align === align }))}
          </Row>
          <Row>
            <select
              className="form-select w-25"
              aria-label="Font size"
              title="Font size"
              value={state.size}
              onChange={(event) => actions.size(event.target.value)}
            >
              <option value="">Font Size</option>
              {Object.values(FontSizes).map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <select
              className="form-select flex-grow-1 w-auto"
              aria-label="Font family"
              title="Font family"
              value={state.family}
              onChange={(event) => actions.family(event.target.value)}
            >
              <option value="">Font Family</option>
              {Object.values(FontFamilies).map((family) => (
                <option key={family} value={family}>
                  {familyName(family)}
                </option>
              ))}
            </select>
          </Row>
          <Row>
            {Toggles.map(({ key, label: name, letter, className }) => (
              <button
                key={key}
                type="button"
                className={state[key] ? BarButtonCurrent : BarButton}
                aria-pressed={state[key]}
                aria-label={name}
                title={name}
                onClick={() => actions.toggle(key)}
                {...keepFocus}
              >
                <span className={classNames("fs-6", className)}>{letter}</span>
              </button>
            ))}
            {button("Strikethrough", StrikethroughIcon, () => actions.toggle("strike"), { pressed: state.strike })}
            <RichTextColourPicker
              label="Text colour"
              clearLabel="Clear colour"
              icon={TextIcon}
              initial="#000000"
              current={state.colour}
              buttonClassName={BarButton}
              onPick={actions.colour}
            />
            <RichTextColourPicker
              label="Background colour"
              clearLabel="Clear background"
              icon={HighlightIcon}
              initial="#ffff00"
              current={state.background}
              buttonClassName={BarButton}
              onPick={actions.background}
            />
          </Row>
          <Row>
            {button("Bullet list", BulletListIcon, () => actions.list("UL"), { pressed: state.list === "UL" })}
            {button("Numbered list", NumberedListIcon, () => actions.list("OL"), { pressed: state.list === "OL" })}
            <button type="button" className={BarButton} aria-label="Link" title="Link" onClick={actions.link} {...keepFocus}>
              <FontAwesomeIcon icon={LinkIcon} className="fa-lg" />
              Link
            </button>
            <button type="button" className={BarButton} aria-label="Unlink" title="Unlink" onClick={actions.unlink} {...keepFocus}>
              <FontAwesomeIcon icon={UnlinkIcon} className="fa-lg" />
              Unlink
            </button>
            {hasAttributes && (
              <button
                type="button"
                className={BarButton}
                aria-label="Add attribute"
                title="Add attribute"
                onClick={actions.attribute}
                {...keepFocus}
              >
                <FontAwesomeIcon icon={AttributeIcon} className="fa-lg" />
                Add attribute
              </button>
            )}
          </Row>
        </>
      )}
    </div>
  );
}
